import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

/**
 * Bounded retry for the browser → GCS signed-URL PUT.
 *
 * Context (ENG-4597): payslip batches across ~14+ employers were failing with
 * "Document failed to upload to GCP Bucket" because the dashboard performed a
 * single-shot PUT to the signed URL — any transient network blip or GCS 5xx
 * failed the entire batch and the user had to start over.
 *
 * Retry policy:
 * - Retries ONLY transient failures: network errors (no response received),
 *   HTTP 408 (request timeout), 429 (rate limited) and any 5xx (GCS returns
 *   500/502/503 for transient storage errors).
 * - Never retries other 4xx responses — an expired signed URL (403) or a
 *   content-length-range violation (400) will fail identically on retry.
 * - Never retries user-initiated aborts/cancellations.
 * - Exponential backoff with full jitter, capped, so a struggling network or
 *   a throttling bucket isn't hammered.
 */

/** Total attempts = 1 initial + 3 retries. */
export const DEFAULT_MAX_ATTEMPTS = 4;

export const BASE_DELAY_MS = 1_000;
export const MAX_DELAY_MS = 8_000;

/**
 * True when the error is worth retrying: a network-level failure (no HTTP
 * response) or a transient HTTP status (408 / 429 / 5xx). Cancellations and
 * all other 4xx responses are terminal.
 */
export const isTransientUploadError = (err: unknown): boolean => {
  if (axios.isCancel(err)) {
    return false;
  }
  if (!axios.isAxiosError(err)) {
    return false;
  }
  // Some axios versions surface AbortController aborts as ERR_CANCELED axios
  // errors rather than Cancel objects — treat those as terminal too.
  if (err.code === "ERR_CANCELED") {
    return false;
  }
  const status = err.response?.status;
  if (status === undefined) {
    // No response — DNS failure, connection reset, mid-transfer drop, timeout.
    return true;
  }
  return status === 408 || status === 429 || status >= 500;
};

/**
 * Exponential backoff with full jitter: attempt 1 → ≤1s, 2 → ≤2s, 3 → ≤4s,
 * capped at MAX_DELAY_MS. A minimum floor of half the window avoids
 * effectively-zero delays from unlucky jitter rolls.
 */
export const backoffDelayMs = (
  attempt: number,
  random: () => number = Math.random
): number => {
  const window = Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS);
  return window / 2 + random() * (window / 2);
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface SignedUrlUploadRetryOptions {
  maxAttempts?: number;
  /**
   * Invoked before each retry sleep — use it to reset progress UI and/or log.
   * `attempt` is the attempt number that just failed (1-based).
   */
  onRetry?: (attempt: number, error: unknown) => void;
}

/**
 * PUT `file` to a GCS signed URL, retrying transient failures with
 * exponential backoff. Resolves with the axios response of the first
 * successful attempt; rethrows the last error when attempts are exhausted or
 * the error is terminal (4xx, cancellation).
 */
export const putToSignedUrlWithRetry = async (
  signedUrl: string,
  file: File | Blob | undefined,
  config: AxiosRequestConfig,
  options: SignedUrlUploadRetryOptions = {}
): Promise<AxiosResponse> => {
  const maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;

  let attempt = 0;
  for (;;) {
    attempt += 1;
    try {
      // eslint-disable-next-line no-await-in-loop
      return await axios.put(signedUrl, file, config);
    } catch (err) {
      if (attempt >= maxAttempts || !isTransientUploadError(err)) {
        throw err;
      }
      options.onRetry?.(attempt, err);
      // eslint-disable-next-line no-await-in-loop
      await sleep(backoffDelayMs(attempt));
    }
  }
};
